<script setup>
import VDevider from "@/Shared/VDevider.vue";
import VButtonSubmit from "@/Shared/Buttons/VButtonSubmit";
import VRadioWithLabel from "../../Form/VRadioWithLabel.vue";
import VInputWithLabel from "../../Form/VInputWithLabel.vue";
import VRadioTextWithBlockLabel from "../../Form/Survey/VRadioTextWithBlockLabel.vue";

import { useForm } from "@inertiajs/vue3";
import intus from "@/Plugins/intus";
import { isRequired, isMax, isMin, isInteger } from "intus/rules";
import { scrollOnError } from "@/Helpers/validation";
import VSelectDefaultWithLabel from "../../Form/VSelectDefaultWithLabel.vue";
import VSelectDefaultTextWithLabel from "../../Form/VSelectDefaultTextWithLabel.vue";

const props = defineProps({
    additional: Object,
    isShowSubmit: {
        Type: Boolean,
        default: true,
    },
});

const { surveyFields, answers } = props.additional;

const form = useForm({
    survey_location: answers?.survey_location,
    nationality: answers?.nationality,
    country_of_residence: answers?.country_of_residence,
    gender: answers?.gender,
    marital_status: answers?.marital_status,
    age: parseInt(answers?.age),
    current_employment: answers?.current_employment,
});

form.setError(props.additional.errors);

const emits = defineEmits("onNext");

const handleClickNext = () => {
    const validation = intus.validate(
        form.data(),
        {
            survey_location: [isRequired()],
            nationality: [isRequired()],
            country_of_residence: [isRequired()],
            gender: [isRequired()],
            marital_status: [isRequired()],
            age: [isRequired(), isInteger(), isMin(18)],
            current_employment: [isRequired()],
        },
        {
            ...intus.messages,
            survey_location: surveyFields["survey_location"]?.name,
            nationality: surveyFields["nationality"]?.name,
            country_of_residence: surveyFields["country_of_residence"]?.name,
            gender: surveyFields["gender"]?.name,
            marital_status: surveyFields["marital_status"]?.name,
            age: surveyFields["age"]?.name,
            current_employment: surveyFields["current_employment"]?.name,
        }
    );

    form.clearErrors();
    if (validation.passes()) {
        emits("onNext", form.data());
    } else {
        form.setError(validation.errors());
        setTimeout(() => scrollOnError(), 300);
    }
};

const widthLabel = 4;
const widthInput = 8;
</script>

<template>
    <div>
        <div class="row">
            <div class="col-lg-12 mb-3">
                <div class="mb-3">
                    <VSelectDefaultWithLabel
                        elId="survey_location"
                        v-model:value="form.survey_location"
                        :label="surveyFields['survey_location']?.name"
                        :options="
                            surveyFields['survey_location']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :error="form.errors?.survey_location"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VSelectDefaultTextWithLabel
                        elId="nationality"
                        v-model:value="form.nationality"
                        :label="surveyFields['nationality']?.name"
                        :options="
                            surveyFields['nationality']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :otherOptionLabel="
                            surveyFields['nationality']?.config['other_label']
                        "
                        :otherOptionPlaceholder="
                            surveyFields['nationality']?.config[
                                'other_placehholder'
                            ]
                        "
                        :error="form.errors?.nationality"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VSelectDefaultTextWithLabel
                        elId="country_of_residence"
                        v-model:value="form.country_of_residence"
                        :label="surveyFields['country_of_residence']?.name"
                        :options="
                            surveyFields['country_of_residence']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :otherOptionLabel="
                            surveyFields['country_of_residence']?.config[
                                'other_label'
                            ]
                        "
                        :otherOptionPlaceholder="
                            surveyFields['country_of_residence']?.config[
                                'other_placehholder'
                            ]
                        "
                        :error="form.errors?.country_of_residence"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VRadioWithLabel
                        elId="gender"
                        v-model:value="form.gender"
                        :label="surveyFields['gender']?.name"
                        :options="
                            surveyFields['gender']?.options.map((value) => {
                                return { id: value, description: value };
                            })
                        "
                        :error="form.errors?.gender"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VRadioTextWithBlockLabel
                        elId="marital_status"
                        v-model:value="form.marital_status"
                        :label="surveyFields['marital_status']?.name"
                        layout="vertical"
                        :options="
                            surveyFields['marital_status']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :otherOptionLabel="
                            surveyFields['marital_status']?.config[
                                'other_label'
                            ]
                        "
                        :error="form.errors?.marital_status"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VInputWithLabel
                        elId="age"
                        v-model:value="form.age"
                        :label="surveyFields['age']?.name"
                        type="number"
                        :error="form.errors?.age"
                        :additionalAttr="{ max: 100, min: 7 }"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>

                <div class="mb-3">
                    <VRadioTextWithBlockLabel
                        elId="current_employment"
                        v-model:value="form.current_employment"
                        :label="surveyFields['current_employment']?.name"
                        layout="vertical"
                        :options="
                            surveyFields['current_employment']?.options.map(
                                (value) => {
                                    return { id: value, description: value };
                                }
                            )
                        "
                        :otherOptionLabel="
                            surveyFields['current_employment']?.config[
                                'other_label'
                            ]
                        "
                        :error="form.errors?.current_employment"
                        :widthLabel="widthLabel"
                        :widthInput="widthInput"
                    />
                </div>
            </div>
        </div>

        <VDevider />
        <div class="text-end" v-if="isShowSubmit">
            <VButtonSubmit type="button" @onCLickSubmit="handleClickNext">
                {{ $t("public.page.survey.button.next") }}
            </VButtonSubmit>
        </div>
    </div>
</template>
